import { useState, useEffect } from 'react';
import { MessageSquare, ChevronRight, Bell, BellOff, AlertTriangle, FileText, Info, Building, Check, Plus } from 'lucide-react';
import { useChat } from '../contexts/ChatContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../lib/services';

interface MessagesViewProps {
  onNavigate: (view: string) => void;
}

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  actionUrl: string | null;
  metadata: unknown;
  createdAt: string;
}

export default function MessagesView({ onNavigate }: MessagesViewProps) {
  const { conversations } = useChat();
  const { getUserReplicas, getReplicaById, userReplicas: allReplicas } = useReplica();
  const { user } = useAuth();

  // Use getUserReplicas first; fall back to all replicas from context if empty (Firestore sync timing)
  const filteredReplicas = user?.uid ? (getUserReplicas(user.uid) ?? []) : [];
  const userReplicas = filteredReplicas.length > 0 ? filteredReplicas : allReplicas;

  // Tab state
  const [activeTab, setActiveTab] = useState<'conversations' | 'notifications'>('conversations');

  // Notification state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);

  // Fetch notifications
  useEffect(() => {
    if (activeTab === 'notifications') {
      setNotifLoading(true);
      notificationService.list({ limit: 20 }).then(res => {
        if (res.data) {
          setNotifications(res.data.notifications);
          setUnreadCount(res.data.unreadCount);
        }
      }).catch(() => { }).finally(() => setNotifLoading(false));
    }
  }, [activeTab]);

  // Also fetch unread count on mount
  useEffect(() => {
    notificationService.list({ limit: 1, unread: true }).then(res => {
      if (res.data) {
        setUnreadCount(res.data.unreadCount);
      }
    }).catch(() => { });
  }, []);

  const handleMarkRead = async (id: string) => {
    await notificationService.markRead(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllRead = async () => {
    await notificationService.markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  // Format relative time
  const formatTime = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getNotifIcon = (type: string) => {
    switch (type) {
      case 'escalation': return <AlertTriangle size={16} className="text-amber-400" />;
      case 'contract': return <FileText size={16} className="text-hig-accent" />;
      case 'workspace': return <Building size={16} className="text-purple-400" />;
      default: return <Info size={16} className="text-hig-accent" />;
    }
  };

  // HR Mark state colors for conversation avatars
  const CORNER_COLORS: Record<string, string> = {
    default:   'var(--accent)',
    thinking:  'var(--accent)',
    listening: 'var(--accent)',
    approval:  '#FFB800',
    escalated: '#FF003C',
    success:   '#00FF66',
    sovereign: '#F1F5F9',
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-hig-text-primary tracking-tight">Messages</h1>
          <p className="text-hig-text-secondary mt-2">
            Chat with your Replicas and manage notifications
          </p>
        </div>
        {/* New Conversation button — always visible */}
        <button
          onClick={() => {
            // Gather known replicas: from context first, then from conversation history as fallback
            const knownReplicas = userReplicas.length > 0
              ? userReplicas
              : [...new Set(conversations.map(c => c.replicaId))].filter(Boolean).map(id => ({ id }));

            if (knownReplicas.length === 1) {
              // Single replica — go directly to chat
              onNavigate(`chat-${knownReplicas[0].id}`);
            } else if (knownReplicas.length > 1 && userReplicas.length > 0) {
              // Multiple replicas loaded — scroll to quick-start picker
              document.getElementById('messages-quick-start')?.scrollIntoView({ behavior: 'smooth' });
            } else if (knownReplicas.length > 0) {
              // Multiple replicas from conversations — open the most recent one
              onNavigate(`chat-${knownReplicas[0].id}`);
            } else {
              // Truly no replicas — go to Work page to create one
              onNavigate('work');
            }
          }}
          className="btn-primary flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex-shrink-0"
        >
          <Plus size={16} />
          New Conversation
        </button>
      </div>

      {/* Tab Bar — brand active pattern */}
      <div className="flex gap-0">
        <button
          onClick={() => setActiveTab('conversations')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm transition-all rounded-t-lg ${
            activeTab === 'conversations'
              ? 'bg-hig-accent/[0.08] text-hig-accent font-semibold border-b-[1.5px] border-hig-accent'
              : 'text-hig-text-tertiary font-normal border-b-[1.5px] border-transparent hover:text-hig-text-secondary hover:bg-white/[0.04]'
          }`}
        >
          <MessageSquare size={16} />
          Conversations
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm transition-all rounded-t-lg ${
            activeTab === 'notifications'
              ? 'bg-hig-accent/[0.08] text-hig-accent font-semibold border-b-[1.5px] border-hig-accent'
              : 'text-hig-text-tertiary font-normal border-b-[1.5px] border-transparent hover:text-hig-text-secondary hover:bg-white/[0.04]'
          }`}
        >
          <Bell size={16} />
          Notifications
          {unreadCount > 0 && (
            <span className="ml-1 px-2 py-0.5 text-[10px] font-bold bg-hig-accent text-hig-bg-primary rounded-full">
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Conversations Tab */}
      {activeTab === 'conversations' && (
        <>
          {/* Quick Start - Show replicas without conversations */}
          {userReplicas.length > 0 && (
            <div id="messages-quick-start" className="card p-6">
              <h2 className="text-sm font-semibold text-hig-text-secondary uppercase tracking-wider mb-4">
                Start a Conversation
              </h2>
              <div className="flex flex-wrap gap-3">
                {userReplicas.map((replica) => {
                  const qsCornerColor = (replica as any)?.state
                    ? (CORNER_COLORS[(replica as any).state] ?? 'var(--accent)')
                    : 'var(--accent)';
                  return (
                    <button
                      key={replica.id}
                      onClick={() => onNavigate(`chat-${replica.id}`)}
                      className="flex items-center gap-3 px-4 py-3 bg-hig-bg-tertiary hover:bg-hig-accent/10 border border-hig-divider hover:border-hig-accent/30 rounded-xl transition-all"
                    >
                      <div style={{ position: 'relative', width: 32, height: 32, flexShrink: 0 }}>
                        <svg width="32" height="32" viewBox="0 0 100 100">
                          <rect x="8" y="8" width="84" height="84" rx="18" fill="none" stroke="var(--hr-frame)" strokeWidth="8" />
                          <path d="M8 52 L8 74 Q8 92 26 92 L44 92" fill="none" stroke={qsCornerColor} strokeWidth="7" strokeLinecap="round" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-hig-text-primary">{replica.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Conversation List */}
          <div className="card overflow-hidden">
            <div className="p-5 border-b border-hig-divider">
              <div className="flex items-center gap-3">
                <MessageSquare size={18} className="text-hig-accent" />
                <h2 className="text-sm font-semibold text-hig-text-secondary tracking-tight">
                  Recent Conversations
                </h2>
              </div>
            </div>

            {conversations.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-16 h-16 rounded-2xl bg-hig-accent/10 flex items-center justify-center mx-auto mb-4">
                  <MessageSquare size={28} className="text-hig-accent" />
                </div>
                <h3 className="font-semibold text-hig-text-primary mb-2">No conversations yet</h3>
                <p className="text-sm text-hig-text-tertiary max-w-sm mx-auto mb-6">
                  {userReplicas.length > 0
                    ? 'Select one of your Replicas above to begin a conversation.'
                    : 'Create a Replica first, then come back here to start chatting.'}
                </p>
                {userReplicas.length === 0 && (
                  <button
                    onClick={() => onNavigate('work')}
                    className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
                  >
                    <Plus size={16} />
                    Go to My Replicas
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-hig-divider">
                {conversations.map((convo) => {
                  const replica = getReplicaById(convo.replicaId);
                  // Debug: log replica object shape to verify available fields
                  if (replica) console.log('[MessagesView] replica object:', { id: replica.id, name: replica.name, keys: Object.keys(replica) });
                  const name = replica?.name || (replica as any)?.displayName || (replica as any)?.formData?.name || convo.title || 'Replica';
                  const role = (replica as any)?.role || (replica as any)?.type || '';
                  const cornerColor = (replica as any)?.state
                    ? (CORNER_COLORS[(replica as any).state] ?? 'var(--accent)')
                    : 'var(--accent)';
                  return (
                    <button
                      key={convo.id}
                      onClick={() => onNavigate(`chat-${convo.replicaId}`)}
                      className="w-full flex items-center justify-between text-left transition-colors px-4 py-3 border-b border-white/[0.05] cursor-pointer hover:bg-white/[0.03]"
                    >
                      <div className="flex items-center gap-4">
                        {/* HR Mark Avatar */}
                        <div style={{ position: 'relative', width: 44, height: 44, flexShrink: 0 }}>
                          <svg width="44" height="44" viewBox="0 0 100 100">
                            <rect x="8" y="8" width="84" height="84" rx="18" fill="none" stroke="var(--hr-frame)" strokeWidth="8" />
                            <path d="M8 52 L8 74 Q8 92 26 92 L44 92" fill="none" stroke={cornerColor} strokeWidth="7" strokeLinecap="round" />
                          </svg>
                          {/* Status dot */}
                          <div style={{
                            position: 'absolute',
                            bottom: -1,
                            right: -1,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            border: '2px solid var(--bg-primary)',
                            background: cornerColor,
                          }} />
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold text-hig-text-primary">{name}</p>
                          {role && (
                            <div className="text-[10px] text-hig-text-tertiary tracking-wide uppercase mt-px">
                              {role}
                            </div>
                          )}
                          <p className="text-xs text-hig-text-tertiary whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-80px)] mt-0.5">
                            {convo.lastMessage || 'No messages yet'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-[11px] text-hig-text-tertiary flex-shrink-0">
                          {formatTime(convo.lastMessageAt)}
                        </span>
                        <ChevronRight size={14} className="text-hig-text-tertiary opacity-50 flex-shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="card overflow-hidden">
          <div className="p-5 border-b border-hig-divider flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell size={18} className="text-hig-accent" />
              <h2 className="text-lg font-bold text-hig-text-primary tracking-tight">
                Notifications
              </h2>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-hig-accent hover:text-hig-accent/80 transition-colors flex items-center gap-1.5"
              >
                <Check size={14} />
                Mark all read
              </button>
            )}
          </div>

          {notifLoading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-2 border-hig-accent border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-hig-text-tertiary">Loading notifications...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-hig-bg-tertiary flex items-center justify-center mx-auto mb-4">
                <BellOff size={28} className="text-hig-text-tertiary" />
              </div>
              <h3 className="font-semibold text-hig-text-primary mb-2">All caught up</h3>
              <p className="text-sm text-hig-text-tertiary max-w-sm mx-auto">
                You'll see escalations, contract updates, and system notifications here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-hig-divider">
              {notifications.map((notif) => (
                <button
                  key={notif.id}
                  onClick={() => {
                    if (!notif.read) handleMarkRead(notif.id);
                    if (notif.actionUrl) onNavigate(notif.actionUrl);
                  }}
                  className={`w-full p-5 flex items-start gap-4 hover:bg-hig-bg-elevated/50 transition-colors text-left ${!notif.read ? 'bg-hig-accent/5' : ''
                    }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${notif.type === 'escalation' ? 'bg-amber-400/10' :
                      notif.type === 'contract' ? 'bg-hig-accent/10' :
                        notif.type === 'workspace' ? 'bg-purple-400/10' :
                          'bg-hig-accent/10'
                    }`}>
                    {getNotifIcon(notif.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-hig-text-primary text-sm truncate">{notif.title}</p>
                      {!notif.read && (
                        <span className="w-2 h-2 bg-hig-accent rounded-full flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-hig-text-tertiary line-clamp-2">{notif.body}</p>
                  </div>
                  <span className="text-xs text-hig-text-tertiary flex-shrink-0 mt-1">
                    {formatTime(notif.createdAt)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
